import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import SbCardView from "@/components/SkipBoCard";
import InviteFriends from "@/components/InviteFriends";
import DealAnimation, { WinAnimation, PlayFlightLayer } from "@/components/GameAnimations";
import QuickChat, { ReactionBubble, useReactions } from "@/components/QuickChat";
import { useAuth } from "@/context/AuthContext";
import { wsUrl } from "@/lib/api";
import { SKIPBO, GAME } from "@/constants/testIds";

export default function SkipBoGame({ state, send, wsReady, lastReaction }) {
  const { user } = useAuth();
  const skin = user?.active_sb_skin || "sb_classic";
  const nav = useNavigate();
  const [selectedSource, setSelectedSource] = useState(null); // {type: 'hand'|'stockpile'|'discard', idx}
  const [discardMode, setDiscardMode] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [chatText, setChatText] = useState("");
  const chatEndRef = useRef(null);

  const me = useMemo(() => state?.players.find(p => p.user_id === user?.user_id), [state, user]);
  const myIdx = useMemo(() => state ? state.players.findIndex(p => p.user_id === user?.user_id) : -1, [state, user]);
  const isMyTurn = state && myIdx >= 0 && state.current_player_idx === myIdx && state.status === "playing";
  const isHost = state && state.host_id === user?.user_id;
  const opponents = state.players.filter(p => p.user_id !== user?.user_id);

  useEffect(() => {
    if (chatOpen && chatEndRef.current) chatEndRef.current.scrollIntoView({ behavior: "smooth" });
  }, [state?.chat, chatOpen]);

  // Deal animation on game start
  const [dealActive, setDealActive] = useState(false);
  const prevStatusRef = useRef(state?.status);
  useEffect(() => {
    if (prevStatusRef.current === "waiting" && state?.status === "playing") {
      setDealActive(true);
    }
    prevStatusRef.current = state?.status;
  }, [state?.status]);

  // Watch last_action to animate a card flying from the player to the build pile.
  const lastActionRef = useRef(null);
  const [playFlights, setPlayFlights] = useState([]);
  useEffect(() => {
    const la = state?.last_action;
    if (!la) return;
    const sig = JSON.stringify(la);
    if (sig === lastActionRef.current) return;
    lastActionRef.current = sig;
    if ((la.type === "play" || la.type === "end_turn") && la.card) {
      const id = Math.random().toString(36).slice(2);
      setPlayFlights(prev => [...prev, { id, card: la.card, fromUserId: la.user_id, variant: "skipbo" }]);
    }
  }, [state?.last_action]);
  const dequeueFlight = (id) => setPlayFlights(prev => prev.filter(p => p.id !== id));

  // Quick chat reactions
  const [reactions, pushReaction] = useReactions();
  useEffect(() => { if (lastReaction) pushReaction(lastReaction); }, [lastReaction, pushReaction]);

  // Typed chat messages also bubble above the sender's avatar
  const chatSeenRef = useRef(0);
  useEffect(() => {
    if (!state?.chat || state.chat.length === 0) { chatSeenRef.current = state?.chat?.length || 0; return; }
    if (state.chat.length <= chatSeenRef.current) return;
    const fresh = state.chat.slice(chatSeenRef.current);
    chatSeenRef.current = state.chat.length;
    for (const m of fresh) {
      if (!m || !m.user_id) continue;
      pushReaction({
        user_id: m.user_id,
        emoji: "💬",
        text: (m.text || "").slice(0, 60),
      });
    }
  }, [state?.chat, pushReaction]);

  const chat = state?.chat || [];

  const selectHand = (i) => {
    if (!isMyTurn) return;
    setSelectedSource({ type: "hand", idx: i });
  };
  const selectStockpile = () => {
    if (!isMyTurn) return;
    setDiscardMode(false);
    setSelectedSource({ type: "stockpile" });
  };
  const selectDiscard = (i) => {
    if (!isMyTurn) return;
    setDiscardMode(false);
    setSelectedSource({ type: "discard", idx: i });
  };

  const playToBuild = (buildIdx) => {
    if (!isMyTurn || !selectedSource) { toast.error("Pick a card first"); return; }
    send({
      type: "sb_play",
      source: selectedSource.type,
      source_idx: selectedSource.idx ?? null,
      build_idx: buildIdx,
    });
    setSelectedSource(null);
  };

  const discardToPile = (discIdx) => {
    if (!isMyTurn || !selectedSource || selectedSource.type !== "hand") {
      toast.error("Select a card from your hand to discard");
      return;
    }
    send({
      type: "sb_end_turn",
      hand_idx: selectedSource.idx,
      discard_idx: discIdx,
    });
    setSelectedSource(null);
    setDiscardMode(false);
  };

  const sendChat = () => {
    if (!chatText.trim()) return;
    send({ type: "chat", text: chatText });
    setChatText("");
  };

  const startGame = () => send({ type: "start" });
  const addBot = () => send({ type: "add_bot" });
  const restart = () => send({ type: "restart" });

  return (
    <div className="min-h-screen flex flex-col relative overflow-hidden" data-testid={SKIPBO.container}>
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-2 px-3 sm:px-6 py-2 sm:py-3 border-b border-white/5 glass">
        <div className="flex items-center gap-2 sm:gap-4">
          <button onClick={() => nav("/lobby", { replace: true })} data-testid={GAME.leaveBtn}
            className="text-[10px] sm:text-xs uppercase tracking-[0.2em] text-white/70 hover:text-white">← Leave</button>
          <div className="flex items-center gap-2">
            <span className="text-[10px] sm:text-xs uppercase tracking-[0.2em] text-white/50">Room</span>
            <button
              data-testid={GAME.copyRoomCodeBtn}
              onClick={() => { navigator.clipboard?.writeText(state.code); toast.success("Code copied"); }}
              className="font-display text-sm sm:text-lg tracking-[0.2em] sm:tracking-[0.3em] px-2 sm:px-3 py-1 rounded-md bg-white/5 border border-white/10 hover:bg-white/10"
            >{state.code}</button>
          </div>
          <div className="text-[10px] sm:text-xs uppercase tracking-[0.15em] text-white/50 px-2 py-0.5 rounded-full bg-[color:var(--uno-blue)]/20 border border-[color:var(--uno-blue)]/40">Skip-Bo</div>
        </div>
        <div className="flex items-center gap-2 text-[10px] sm:text-xs uppercase tracking-[0.15em] text-white/60">
          <span>Deck {state.draw_count}</span>
          {/* Chat + React buttons moved to floating dock at bottom-right */}
        </div>
      </div>

      {/* Waiting room */}
      {state.status === "waiting" && (
        <div className="flex-1 flex items-start justify-center p-4 sm:p-6 overflow-y-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 max-w-5xl w-full">
            <div className="glass rounded-2xl p-6 sm:p-8 lg:col-span-2">
              <div className="text-xs uppercase tracking-[0.3em] text-white/50">Waiting room · Skip-Bo</div>
              <div className="font-display text-2xl sm:text-3xl mt-2">Share code <span style={{color:"var(--uno-yellow)"}}>{state.code}</span></div>
              <p className="text-white/60 mt-2 text-sm sm:text-base">Skip-Bo: empty your stockpile first to win. Build 1→12 in the middle.</p>

              {/* Stockpile size selector */}
              <div className="mt-4">
                <div className="text-[11px] uppercase tracking-[0.25em] text-white/50 font-semibold mb-2">Stockpile size</div>
                <div className="flex flex-wrap gap-2">
                  {[10, 15, 20, 30].map(size => {
                    const active = (state.sb_stockpile_size || 20) === size;
                    return (
                      <button
                        key={size}
                        data-testid={`sb-stockpile-${size}`}
                        onClick={() => isHost && send({ type: "set_sb_stockpile", size })}
                        disabled={!isHost}
                        className={`px-4 py-1.5 rounded-full text-sm font-semibold transition ${
                          active
                            ? "bg-[color:var(--uno-yellow)] text-black"
                            : "bg-white/5 text-white/70 hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-60"
                        }`}
                      >
                        {size} cards
                        {size === 10 && <span className="ml-1 text-[10px] opacity-70">· fast</span>}
                        {size === 30 && <span className="ml-1 text-[10px] opacity-70">· long</span>}
                      </button>
                    );
                  })}
                </div>
                {!isHost && <div className="text-[11px] text-white/40 mt-1">Only the host can change this.</div>}
              </div>

              <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-3">
                {state.players.map(p => (
                  <div key={p.user_id} className="rounded-xl border border-white/10 p-3 flex flex-col items-center bg-black/20">
                    <img src={p.avatar} alt="" className="w-12 h-12 sm:w-14 sm:h-14 rounded-full border border-white/20"/>
                    <div className="mt-2 text-xs sm:text-sm font-semibold">{p.name}</div>
                    <div className="text-[10px] uppercase tracking-widest text-white/50">{p.is_bot ? "Bot" : (p.user_id === state.host_id ? "Host" : "Player")}</div>
                  </div>
                ))}
                {Array.from({ length: state.max_players - state.players.length }).map((_,i) => (
                  <div key={"empty"+i} className="rounded-xl border border-dashed border-white/10 p-3 flex items-center justify-center text-white/30 min-h-[92px]">Empty</div>
                ))}
              </div>
              <div className="mt-5 flex flex-wrap gap-3">
                {isHost && state.players.length < state.max_players && (
                  <button data-testid={GAME.addBotBtn} onClick={addBot} className="px-5 py-2.5 rounded-full bg-white/10 hover:bg-white/20 text-white text-sm font-semibold">+ Add bot</button>
                )}
                {isHost && (
                  <button data-testid={GAME.startBtn} onClick={startGame} disabled={state.players.length < 2}
                    className="px-6 py-2.5 rounded-full bg-[color:var(--uno-red)] text-white text-sm font-semibold hover:brightness-110 disabled:opacity-40">
                    Start Skip-Bo
                  </button>
                )}
                {!isHost && <div className="text-white/60 text-sm self-center">Waiting for host…</div>}
              </div>
            </div>

            <InviteFriends
              roomId={state.room_id}
              playerUserIds={state.players.filter(p => !p.is_bot).map(p => p.user_id)}
            />
          </div>
        </div>
      )}

      {/* Playing / Finished */}
      {state.status !== "waiting" && (
        <div className="flex-1 flex flex-col p-2 sm:p-4 gap-2 sm:gap-4">

          {/* Opponents strip */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {opponents.map(p => (
              <div key={p.user_id} className={`relative rounded-2xl p-3 border-2 ${state.players[state.current_player_idx]?.user_id === p.user_id ? "border-[color:var(--uno-yellow)] bg-[color:var(--uno-yellow)]/5" : "border-white/10 bg-black/20"}`}>
                <ReactionBubble reaction={reactions[p.user_id]} />
                <div className="flex items-center gap-3">
                  <img src={p.avatar} alt="" className="w-10 h-10 sm:w-12 sm:h-12 rounded-full border-2 border-white/20"/>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm sm:text-base font-semibold truncate">{p.name}{p.is_bot && <span className="text-white/40 uppercase text-[9px] ml-1">bot</span>}</div>
                    <div className="text-[11px] sm:text-xs uppercase tracking-widest text-white/70 mt-0.5">
                      <span className="text-white font-bold">Stock ×{p.stockpile_count}</span>
                      <span className="text-white/40"> · Hand {p.hand_count}</span>
                    </div>
                  </div>
                  {p.stockpile_top && (
                    <div className="ml-auto"><SbCardView card={p.stockpile_top} size="sm" skin={skin}/></div>
                  )}
                </div>
                {/* Opponent discard piles */}
                <div className="mt-2 grid grid-cols-4 gap-1.5">
                  {p.discard_piles.map((pile, i) => (
                    <div key={i} className="h-12 sm:h-14 rounded-md bg-white/5 border border-white/10 flex items-center justify-center relative">
                      {pile.length > 0 ? <SbCardView card={pile[pile.length-1]} size="xs" skin={skin}/> : <span className="text-[10px] text-white/30">·</span>}
                      {pile.length > 1 && <span className="absolute top-0 right-0 text-[9px] font-bold bg-black/80 rounded-bl px-1">{pile.length}</span>}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Center: build piles */}
          <div className="flex-1 flex flex-col items-center justify-center gap-3">
            <div className="text-xs sm:text-sm uppercase tracking-[0.25em] text-white/60 font-semibold">Build piles · play 1 → 12</div>
            <div className="grid grid-cols-4 gap-3 sm:gap-6">
              {state.build_piles.map((pile, i) => {
                const expected = pile.length + 1;
                return (
                  <button
                    key={i}
                    data-testid={`${SKIPBO.targetBuildPrefix}${i}`}
                    onClick={() => playToBuild(i)}
                    disabled={!isMyTurn || !selectedSource || discardMode}
                    className={`relative rounded-2xl bg-black/30 border-2 p-2 sm:p-3 min-h-[110px] sm:min-h-[160px] flex items-center justify-center ${isMyTurn && selectedSource && !discardMode ? "border-[color:var(--uno-green)] cursor-pointer hover:bg-white/5 shadow-lg shadow-[color:var(--uno-green)]/20" : "border-white/10"} disabled:cursor-not-allowed`}
                  >
                    {pile.length > 0 ? (
                      <>
                        <div className="hidden sm:block"><SbCardView card={pile[pile.length-1]} size="lg" skin={skin}/></div>
                        <div className="sm:hidden"><SbCardView card={pile[pile.length-1]} skin={skin}/></div>
                      </>
                    ) : (
                      <div className="text-center">
                        <div className="text-[11px] uppercase tracking-widest text-white/40">Empty</div>
                        <div className="font-display text-3xl sm:text-5xl text-white/30">1</div>
                      </div>
                    )}
                    {pile.length > 0 && pile.length < 12 && (
                      <div className="absolute -bottom-2.5 right-1 text-[10px] sm:text-xs uppercase tracking-widest bg-black/80 px-2 py-0.5 rounded-full text-white/80 font-semibold">need {expected}</div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* My area */}
          {me && (
            <div className={`rounded-2xl p-2 sm:p-4 border ${isMyTurn ? "border-[color:var(--uno-yellow)] bg-white/[0.03]" : "border-white/10 bg-black/20"}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <img src={me.avatar} alt="" className="w-8 h-8 sm:w-10 sm:h-10 rounded-full border border-white/20"/>
                  <div className="text-xs sm:text-sm font-semibold">{me.name} <span className="text-white/50">· stock {me.stockpile_count}</span></div>
                  {isMyTurn && <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-white text-black font-semibold">Your turn</span>}
                </div>
                {isMyTurn && (
                  <button
                    data-testid={SKIPBO.endTurnBtn}
                    onClick={() => setDiscardMode(m => !m)}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold ${discardMode ? "bg-[color:var(--uno-yellow)] text-black" : "bg-white/10 text-white hover:bg-white/20"}`}
                  >
                    {discardMode ? "Cancel discard" : "End turn (discard)"}
                  </button>
                )}
              </div>

              <div className="grid grid-cols-12 gap-3 items-start">
                {/* Stockpile */}
                <div className="col-span-3 sm:col-span-2">
                  <div className="text-[11px] sm:text-xs uppercase tracking-widest text-white/60 font-semibold mb-1.5">Stockpile</div>
                  <button
                    data-testid={SKIPBO.stockpileBtn}
                    onClick={selectStockpile}
                    disabled={!isMyTurn || !me.stockpile_top}
                    className={`w-full rounded-2xl p-2 bg-black/40 border-2 flex items-center justify-center ${selectedSource?.type === "stockpile" ? "border-[color:var(--uno-green)] shadow-lg shadow-[color:var(--uno-green)]/20" : "border-white/10"} ${isMyTurn && me.stockpile_top ? "hover:border-white/40 cursor-pointer" : "cursor-not-allowed opacity-70"}`}
                  >
                    {me.stockpile_top ? (
                      <div>
                        <div className="hidden sm:block"><SbCardView card={me.stockpile_top} size="md-lg" skin={skin}/></div>
                        <div className="sm:hidden"><SbCardView card={me.stockpile_top} skin={skin}/></div>
                      </div>
                    ) : <div className="text-xs text-white/40 py-6">Empty</div>}
                  </button>
                  <div className="text-center text-xs sm:text-sm font-bold mt-1.5 text-white">×{me.stockpile_count}</div>
                </div>

                {/* Hand */}
                <div className="col-span-9 sm:col-span-7">
                  <div className="text-[11px] sm:text-xs uppercase tracking-widest text-white/60 font-semibold mb-1.5">Hand</div>
                  <div className="flex flex-wrap gap-2 justify-start">
                    {(me.hand || []).map((c, i) => {
                      return (
                      <React.Fragment key={c.id}>
                        <div className="hidden sm:block">
                          <SbCardView
                            testId={`${SKIPBO.handCardPrefix}${i}`}
                            card={c}
                            size="md-lg"
                            skin={skin}
                            playable={isMyTurn}
                            selected={selectedSource?.type === "hand" && selectedSource.idx === i}
                            onClick={() => selectHand(i)}
                          />
                        </div>
                        <div className="sm:hidden">
                          <SbCardView
                            testId={`${SKIPBO.handCardPrefix}${i}-mobile`}
                            card={c}
                            skin={skin}
                            playable={isMyTurn}
                            selected={selectedSource?.type === "hand" && selectedSource.idx === i}
                            onClick={() => selectHand(i)}
                          />
                        </div>
                      </React.Fragment>
                      );
                    })}
                    {(me.hand || []).length === 0 && (
                      <div className="text-white/40 text-sm py-4">Hand empty — will refill on next turn</div>
                    )}
                  </div>
                </div>

                {/* Personal discard piles */}
                <div className="col-span-12 sm:col-span-3">
                  <div className="text-[11px] sm:text-xs uppercase tracking-widest text-white/60 font-semibold mb-1.5">Your discard piles</div>
                  <div className="grid grid-cols-4 gap-2">
                    {me.discard_piles.map((pile, i) => (
                      <button
                        key={i}
                        data-testid={discardMode ? `${SKIPBO.targetDiscardPrefix}${i}` : `${SKIPBO.discardPilePrefix}${i}`}
                        onClick={() => discardMode ? discardToPile(i) : selectDiscard(i)}
                        disabled={!isMyTurn || (!discardMode && pile.length === 0)}
                        className={`rounded-lg bg-black/40 border-2 p-1 min-h-[76px] flex items-center justify-center relative ${
                          discardMode
                            ? "border-[color:var(--uno-red)] hover:bg-white/5 cursor-pointer"
                            : (selectedSource?.type === "discard" && selectedSource.idx === i ? "border-[color:var(--uno-green)] shadow-lg shadow-[color:var(--uno-green)]/20" : "border-white/10 hover:border-white/30")
                        } disabled:opacity-60 disabled:cursor-not-allowed`}
                      >
                        {pile.length > 0 ? <SbCardView card={pile[pile.length-1]} size="sm" skin={skin}/> : <span className="text-[10px] text-white/30">empty</span>}
                        {pile.length > 1 && <span className="absolute top-0 right-0 text-[10px] font-bold bg-black/80 rounded-bl px-1.5 py-0.5">{pile.length}</span>}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Help hint */}
              {isMyTurn && (
                <div className="mt-2 text-[11px] text-white/50">
                  {discardMode
                    ? "Pick a hand card, then click one of your 4 discard piles to end your turn."
                    : selectedSource
                      ? "Selected. Click a build pile in the center (must match next number) or a discard pile to place."
                      : "Tap a hand card, your stockpile top, or a discard pile top to select."}
                </div>
              )}
            </div>
          )}

          {/* Finished — Win animation with confetti */}
          <AnimatePresence>
            {state.status === "finished" && (() => {
              const winner = state.players.find(p => p.user_id === state.winner);
              return (
                <WinAnimation
                  key="win"
                  winnerName={winner?.name || "Someone"}
                  winnerAvatar={winner?.avatar}
                  isMe={state.winner === user?.user_id}
                  subtitle={state.winner === user?.user_id ? "You emptied your stockpile!" : "Coins were added to your balance."}
                  onContinue={() => nav("/lobby", { replace: true })}
                  primaryLabel="Play again"
                  onPrimary={restart}
                  canPrimary={isHost}
                />
              );
            })()}
          </AnimatePresence>
        </div>
      )}

      {/* Skip-Bo deal animation (fires once when game starts) */}
      <DealAnimation
        active={dealActive}
        players={state.players}
        myUserId={user?.user_id}
        cardsPerPlayer={5}
        onDone={() => setDealActive(false)}
        variant="skipbo"
      />

      {/* Card-play flight layer */}
      <PlayFlightLayer
        plays={playFlights}
        players={state.players}
        myUserId={user?.user_id}
        renderCard={(card) => <SbCardView card={card} size="lg" skin={skin}/>}
        onFinished={dequeueFlight}
      />

      {/* Self-reaction bubble (floats just left of the react button) */}
      <AnimatePresence>
        {reactions[user?.user_id] && (
          <motion.div
            key={reactions[user?.user_id].id}
            initial={{ opacity: 0, x: 20, scale: 0.6 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            exit={{ opacity: 0, x: 20, scale: 0.9 }}
            transition={{ type: "spring", stiffness: 320, damping: 20 }}
            className="fixed top-1/2 -translate-y-1/2 right-16 sm:right-20 z-40 pointer-events-none max-w-[60vw]"
          >
            <div className="bg-white text-black rounded-2xl px-3 py-2 shadow-xl flex items-center gap-1.5 border-2 border-[color:var(--uno-yellow)]">
              <motion.span
                animate={{ rotate: [0, -18, 18, 0], scale: [1, 1.35, 1.1, 1] }}
                transition={{ duration: 0.75, ease: "easeOut" }}
                className="text-xl leading-none"
              >{reactions[user?.user_id].emoji}</motion.span>
              <span className="text-sm font-bold">{reactions[user?.user_id].text}</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating react button — vertically centered on right edge */}
      <div className="fixed top-1/2 -translate-y-1/2 right-3 sm:right-4 z-30">
        <QuickChat send={send} disabled={state.status !== "playing"} direction="left" />
      </div>

      {/* Chat panel */}
      <AnimatePresence>
        {chatOpen && (
          <motion.aside initial={{x: 380}} animate={{x:0}} exit={{x: 380}} transition={{type:"spring", stiffness:220, damping: 26}}
            className="absolute top-0 right-0 bottom-0 w-full sm:w-[360px] max-w-full z-30 flex flex-col glass border-l border-white/10">
            <div className="p-4 border-b border-white/10 flex items-center justify-between">
              <div className="text-xs uppercase tracking-[0.3em] text-white/60">Table chat</div>
              <button onClick={() => setChatOpen(false)} className="text-white/50 hover:text-white text-sm">✕</button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {chat.map((m, i) => (
                <div key={i} className="flex items-start gap-2">
                  <img src={m.avatar} alt="" className="w-8 h-8 rounded-full mt-0.5 border border-white/10"/>
                  <div>
                    <div className="text-xs text-white/60">{m.name}</div>
                    <div className="text-sm bg-white/5 rounded-xl px-3 py-1.5 inline-block">{m.text}</div>
                  </div>
                </div>
              ))}
              <div ref={chatEndRef}/>
            </div>
            <div className="p-3 border-t border-white/10 flex gap-2">
              <input
                data-testid={GAME.chatInput}
                value={chatText}
                onChange={e => setChatText(e.target.value)}
                onKeyDown={e => e.key === "Enter" && sendChat()}
                placeholder="Say something…"
                className="flex-1 bg-black/40 border border-white/10 rounded-full px-4 py-2 text-sm outline-none focus:border-[color:var(--uno-yellow)]"
              />
              <button data-testid={GAME.chatSendBtn} onClick={sendChat} className="px-4 py-2 rounded-full bg-white text-black text-sm font-semibold">Send</button>
            </div>
          </motion.aside>
        )}
      </AnimatePresence>
    </div>
  );
}
